import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { Dictionary, keyBy } from 'lodash';
import { HolidayCalendarAPI } from 'api/holiday-calendar';
import {
  HolidayCalendar,
  HolidayCalendarPayload,
} from 'api/holiday-calendar/types';
import { oneDay } from './constants';

export const HOLIDAY_CALENDAR_KEY = ['holiday_calendar'];

export const useHolidayCalendars = (
  options: UseQueryOptions<
    Dictionary<HolidayCalendar>,
    unknown,
    Dictionary<HolidayCalendar>,
    typeof HOLIDAY_CALENDAR_KEY
  > = {},
): UseQueryResult<Dictionary<HolidayCalendar>> => {
  return useQuery(
    HOLIDAY_CALENDAR_KEY,
    () =>
      HolidayCalendarAPI.getHolidayCalendars().then((holidayCalendars) =>
        keyBy(holidayCalendars, 'holidayCalendarId'),
      ),
    {
      staleTime: oneDay,
      ...options,
    },
  );
};

export const useHolidayCalendarDeleteMutation = (
  options: UseMutationOptions<unknown, unknown, { calendarId: number }> = {},
): UseMutationResult<unknown, unknown, { calendarId: number }> =>
  useMutation<unknown, unknown, { calendarId: number }>(
    ({ calendarId }) => HolidayCalendarAPI.deleteHolidayCalendar(calendarId),
    options,
  );

export const useHolidayCalendarUpdateMutation = (
  options: UseMutationOptions<HolidayCalendar, unknown, HolidayCalendar> = {},
): UseMutationResult<HolidayCalendar, unknown, HolidayCalendar> =>
  useMutation<HolidayCalendar, unknown, HolidayCalendar>(
    (holidayCalendar: HolidayCalendar) =>
      HolidayCalendarAPI.editHolidayCalendar(holidayCalendar),
    options,
  );

export const useHolidayCalendarCreateMutation = (
  options: UseMutationOptions<
    HolidayCalendar,
    unknown,
    HolidayCalendarPayload
  > = {},
): UseMutationResult<HolidayCalendar, unknown, HolidayCalendarPayload> =>
  useMutation<HolidayCalendar, unknown, HolidayCalendarPayload>(
    (holidayCalendar: HolidayCalendarPayload) =>
      HolidayCalendarAPI.createHolidayCalendar(holidayCalendar),
    options,
  );
